// ─────────────────────────────────────────────────────────────────────────────
// LEARNING AGILITY — Factors
// 5-dimension model inspired by Korn Ferry / Lominger research
// Mental · People · Change · Results · Self-Awareness
// ─────────────────────────────────────────────────────────────────────────────

export const DIMENSIONS = [
  {
    id:       "MA",
    name:     "Mental Agility",
    icon:     "🧠",
    color:    "#1d4ed8",
    bg:       "#eff6ff",
    headline: "Thinks critically · makes fresh connections · comfortable with complexity",
    description: "The ability to think through problems in new and unique ways, question the obvious, and make complex decisions under ambiguity. Mentally agile learners deconstruct situations, find patterns across disparate domains, and resist the pull of familiar solutions.",
    highProfile: "You approach problems from multiple angles simultaneously, challenge assumptions that others accept, and are energised by complex, messy situations rather than drained by them. You synthesise information quickly and produce frameworks where others produce lists.",
    lowProfile: "You may rely on proven methods and structured approaches when ambiguity arises. Novel problems can feel draining. You perform best when the rules are clear and the path is established.",
    developmentTips: [
      "Practise lateral thinking — find three different explanations for any problem before settling on one",
      "Read across domains; borrow frameworks from fields unrelated to yours",
      "Ask 'what am I not seeing?' before moving to solutions",
      "Seek out colleagues who think very differently from you and engage with their reasoning seriously",
    ],
    facets: ["critical_thinking", "complexity_comfort", "pattern_recognition", "questioning_assumptions"],
  },
  {
    id:       "PA",
    name:     "People Agility",
    icon:     "🤝",
    color:    "#7c3aed",
    bg:       "#f5f3ff",
    headline: "Reads situations · learns from others · builds through diversity",
    description: "The ability to understand people deeply, read situations accurately, and learn from a wide range of interpersonal experiences. People-agile leaders bring out the best in others, navigate conflict constructively, and build trust across difference.",
    highProfile: "You tune in quickly to what is happening interpersonally in a room — who is aligned, who is resistant, and what is unspoken. You learn as much from your relationships as from formal training, and you actively seek perspectives that challenge yours.",
    lowProfile: "You may prefer task-focused interaction and find complex relationship dynamics draining. You may underestimate how much interpersonal intelligence influences outcomes, or default to logic when what the situation needs is empathy.",
    developmentTips: [
      "After every significant interaction, ask: what did I learn about this person?",
      "Seek feedback from people who are different from you — culturally, generationally, functionally",
      "Practise listening without preparing your response while the other person speaks",
      "Volunteer for cross-functional roles where the relationship network is entirely new",
    ],
    facets: ["situational_reading", "interpersonal_learning", "diversity_openness", "conflict_navigation"],
  },
  {
    id:       "CA",
    name:     "Change Agility",
    icon:     "⚡",
    color:    "#c8964e",
    bg:       "#fff7ed",
    headline: "Curious · experimenter · energised by the new",
    description: "The extent to which a person is genuinely energised by change, seeks novelty, experiments readily, and tolerates the discomfort of the unfamiliar. Change-agile people treat ambiguity as a resource rather than a threat.",
    highProfile: "You are rarely caught flat-footed by change — you anticipated it, or quietly preferred it. You run experiments before others are ready to commit to a hypothesis. Variety is not just acceptable; it is motivating. You thrive in fast-moving environments.",
    lowProfile: "You may find rapid change disorienting and are at your best when you can establish a reliable rhythm. You may resist new initiatives before seeing proof of value, or prefer to optimise existing systems rather than replace them.",
    developmentTips: [
      "Deliberately try one new approach per week — even in routine tasks",
      "When facing a major change, identify one thing you are curious about rather than one thing you fear",
      "Build small experiments into your workflow — test before you commit",
      "Track what you resist and ask why. Is it logic or discomfort?",
    ],
    facets: ["novelty_seeking", "experimentation", "ambiguity_tolerance", "variety_drive"],
  },
  {
    id:       "RA",
    name:     "Results Agility",
    icon:     "🎯",
    color:    "#dc2626",
    bg:       "#fef2f2",
    headline: "Delivers in first-time situations · inspires others · resourceful under pressure",
    description: "The capacity to deliver results under challenging, first-time, or high-pressure conditions — often with insufficient resources, unclear mandates, or resistant stakeholders. Results-agile people bring focused energy and inspire others to do the same.",
    highProfile: "You are at your best when the path is unclear and the stakes are high. You back yourself in unfamiliar territory, drive performance through others, and find ways through obstacles that would stop others. You raise the energy in a room.",
    lowProfile: "You may deliver reliably in familiar contexts but find it difficult to maintain performance when the conditions change significantly. You may depend on established processes and may hesitate when clear guidance is absent.",
    developmentTips: [
      "Volunteer for stretch assignments — projects outside your current competence",
      "Build the habit of defining success metrics before starting any new initiative",
      "When blocked, identify the smallest possible action you can take in the next hour",
      "Practise inspiring others through clarity of outcome, not just effort",
    ],
    facets: ["first_time_performance", "inspirational_presence", "resourcefulness", "high_pressure_delivery"],
  },
  {
    id:       "SA",
    name:     "Self-Awareness",
    icon:     "🪞",
    color:    "#059669",
    bg:       "#f0fdf4",
    headline: "Knows self accurately · seeks feedback · non-defensive",
    description: "The degree to which a person has an accurate, calibrated understanding of their own strengths and blind spots — and actively seeks information to update that picture. Self-aware learners do not give in to defensive reasoning and treat feedback as data.",
    highProfile: "You have a realistic, nuanced sense of where you are strong, where you are average, and where you genuinely need development. You seek feedback not as validation but as data. When you receive difficult feedback, your first instinct is curiosity, not defence.",
    lowProfile: "You may overestimate your performance in areas of genuine weakness, or have blind spots that others can see clearly. You may find critical feedback difficult to receive non-defensively, or avoid situations that might produce uncomfortable self-knowledge.",
    developmentTips: [
      "After any difficult interaction, ask: what did I contribute to that dynamic?",
      "Build a feedback habit — ask two people per month for specific, behavioural feedback",
      "Map your blind spots: what do people who work with you notice that you rarely talk about?",
      "When you disagree with feedback, sit with it for 48 hours before dismissing it",
    ],
    facets: ["self_knowledge", "feedback_seeking", "defensive_reasoning", "calibration_accuracy"],
  },
];

export const DIM_MAP = Object.fromEntries(DIMENSIONS.map(d => [d.id, d]));

export const LA_BANDS = [
  { min: 8.5, label: "High Agility",     color: "#059669", sub: "Top 10% of talent pool" },
  { min: 7.0, label: "Above Average",    color: "#0891b2", sub: "Upper 25% of talent pool" },
  { min: 5.5, label: "Moderate Agility", color: "#d97706", sub: "Middle 50% of talent pool" },
  { min: 4.0, label: "Developing",       color: "#dc2626", sub: "Lower 25% of talent pool" },
  { min: 0,   label: "Low Agility",      color: "#991b1b", sub: "Significant development needed" },
];

export function getLABand(score) {
  return LA_BANDS.find(b => score >= b.min) || LA_BANDS[LA_BANDS.length - 1];
}

export const OVERALL_FORMULA = {
  MA: 0.22,  // Mental Agility
  PA: 0.20,  // People Agility
  CA: 0.18,  // Change Agility
  RA: 0.22,  // Results Agility
  SA: 0.18,  // Self-Awareness
};

// Norms: [low_norm, high_norm] for each dimension (Likert 1-5 mapped to 1-10)
export const NORMS = {
  MA: [5.5, 7.5],
  PA: [5.5, 7.5],
  CA: [5.0, 7.5],
  RA: [5.5, 7.5],
  SA: [6.0, 8.0],
};
